/**
 * Detection volume by day, stacked by severity band.
 *
 * Form: the job is magnitude over time WITH composition — how many detections
 * arrived each day and how serious they were — which is a stacked bar.
 *
 * Four series, not five. `SEVERITY_COLORS` maps Informational and Low to
 * the same hue, so charting all five would render two series that are
 * literally indistinguishable. They fold into one band here, which also
 * matches BlueSphere's own four-band system (Normal / Elevated / High /
 * Critical) rather than inventing a fifth color.
 *
 * Severity is a STATUS ramp, not a categorical palette: the order carries
 * meaning, so the hues stay in band order and are never reassigned.
 *
 * Accessibility, and why the labels are not optional: the brand ramp's
 * mid-tones sit below 3:1 against a light card (#F9CC00 is 1.5:1), so
 * color alone cannot carry identity here. The legend is always present
 * and the tooltip names every band with its count — identity is never
 * color-only.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { colors } from './tokens.js';

export interface DetectionVolumePoint {
  /** Short axis label, e.g. "09-17". */
  label: string;
  critical: number;
  high: number;
  elevated: number;
  normal: number;
}

export interface DetectionVolumeChartProps {
  data: DetectionVolumePoint[];
  height?: number;
}

/** Stacked bottom-up, least severe first, so Critical reads on top. */
const SERIES = [
  { key: 'normal', name: 'Informational / Low', color: colors.scoreNormal },
  { key: 'elevated', name: 'Medium', color: colors.scoreElevated },
  { key: 'high', name: 'High', color: colors.scoreHigh },
  { key: 'critical', name: 'Critical', color: colors.scoreCritical },
] as const;

export function DetectionVolumeChart({ data, height = 240 }: DetectionVolumeChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        {/* Recessive grid: horizontal only, so the bars carry the eye. */}
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E6EE" vertical={false} />
        <XAxis
          dataKey="label"
          stroke={colors.muted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={colors.muted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <Tooltip
          cursor={{ fill: 'rgba(42,50,65,0.06)' }}
          contentStyle={{
            background: colors.primary,
            border: 'none',
            borderRadius: 12,
            color: colors.textOnDark,
            fontSize: 12,
          }}
          labelStyle={{ color: colors.textOnDark, fontWeight: 600 }}
        />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 12, color: colors.textSecondary, paddingTop: 8 }}
        />
        {SERIES.map((series, index) => (
          <Bar
            key={series.key}
            dataKey={series.key}
            name={series.name}
            stackId="detections"
            fill={series.color}
            // A 2px surface-colored stroke reads as a gap between stacked
            // segments, which is what keeps adjacent bands legible when
            // their hues are close.
            stroke={colors.surface}
            strokeWidth={2}
            // Rounded data-end on the topmost segment only.
            radius={index === SERIES.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
